import { PlatformTypeId } from 'api/Trading-Accounts/Types/PlatformTypeDto'
import { v4 } from 'uuid'

import { NameDto } from '../../Common/Types'
import { BookType } from '../../Trading-Accounts/Types/BookType'
import { ClientRestriction } from './ClientRestriction'
import { TickmillCompanyTypes } from './TickmillCompanyTypes'
import { isCFDProductType, isETDProductType, TickmillProductDto } from './TickmillProductDto'

export interface ClientDetailedDto {
  id: string
  clientAreaId: string
  firstname: string
  middlename: string
  lastname: string
  affiliate: {
    id: string
    name: string
  } | null
  type: {
    id: number
    name: string
  }
  tickmillCompany: {
    id: TickmillCompanyTypes
    name: string
    shortName: string
  }
  status: {
    id: number
    name: string
  }
  nativeName: string | null
  gender: {
    id: number
    name: string
  }
  communicationLanguage: {
    id: string
    name: string
  }
  username: string | null
  nationalClientIdentifier: string | null
  nciType: {
    id: number
    name: string
  } | null
  assignee: {
    id: string
    name: string
    status?: {
      id: number
      name: string
    }
  } | null
  etdAssignee: {
    id: string
    name: string
    status?: {
      id: number
      name: string
    }
  } | null
  birthday: string
  salesStatus: string | null // "new"
  classification: {
    id: number
    name: string
  }
  personalId: string | null
  documentType: {
    id: number
    name: string
  } | null
  documentId: string | null
  documentExpirationDate: string | null
  documentIdInternal: string | null
  marketDataClassification: null | NameDto
  educationLevel: {
    id: number
    name: string
  }
  ownershipStructure: null | NameDto
  employer: string | null
  isEmployed: boolean
  businessNature: {
    id: number
    name: string
  } | null
  isUsCitizen: boolean
  isPoliticallyExposedPerson: boolean
  taxId: string | null
  introducingBroker: null | { id: string; name: string }
  registrationFinishDate: string | null
  isSwapAccount: boolean
  maxMT4Accounts: number
  maxTickmillTraderLiveAccounts: number
  hasSentWelcomeEmail: boolean
  validationCompletedDate: string
  isAddressInfoLocked: boolean
  isBrexitTransferAccepted: boolean
  isFIXApiUser: boolean
  isPersonalInfoLocked: boolean
  isAdditionalInfoLocked: boolean
  isPaymentInfoLocked: boolean
  isExcludedFromLuckyDraws: boolean
  isCompanyInfoLocked: null
  isVerificationExpedited: boolean
  isKYCUpdateNeeded: boolean
  isInternalTestAccount: boolean
  isSwapFreeAccount: boolean
  isWelcomeBonusAccount: boolean
  shouldReceiveNewsletter: boolean
  introducingBrokerRegistrationAllowed: boolean
  hasUploadedDocuments: boolean
  approachedByRep: boolean
  defaultTradingAccountBook: BookType | null
  maxLeverage: {
    id: number
    name: string
  } | null
  brokerIntroductionMethod: {
    id: number
    name: string
  }
  leadId?: string
  detailsLastUpdatedDate: null | string
  companyDetails: CompanyDetails
  nationalities: ClientNationalityDto[]
  phoneNumbers: ClientPhoneNumberDto[]
  emails: ClientEmailDto[]
  teams: ClientLeadTeamDto[]
  addresses: ClientAddressDto[]
  restrictions: ClientRestriction[]
  clientAnswers: ClientAnswerDto[]
  clientPaymentAgent: null | { id: string; name: string }
  clientIntroducingBroker: null | { id: string; name: string }
  clientAmlSummary: null | {
    score: number
    AmlRiskLevel: NameDto
  }
  externalSystems: ExternalSystem[]
  createdDate: string | null
  updatedDate: string | null
  truliooWatchlistStatus: {
    id: number
    name: string
  } | null
  twoFactorAuthTypesAvailableForClientsCountry: {
    isEnabled: boolean
    authMedium: string | null
    isAvailable: boolean
    id: number
    name: string
  }[]
  worldCheckStatus: {
    id: number
    name: string
  } | null
  displayCurrency: {
    id: string
    name: string
  } | null
  preferredCurrency: {
    id: string
    name: string
  } | null
  preferredLandingPage: {
    id: number
    name: string
    tickmillProductId: 1
  } | null
  tickmillPublicDomain: null | string
  tickmillProducts: TickmillProductDto[]
  visibilityConfiguration: {
    hasRegulatoryOptions: boolean
    hasTraderRoom: boolean
    hasIBRoom: boolean
    hasPARoom: boolean
    hasForexTools: boolean
    hasAutochartist: boolean
    hasStocks: boolean
    hasClassification: boolean
    appropriatenessTestCooldownPeriod: number
    kycVerificationProvider: number
    hasDynamicLeverage: boolean
    hasNCI: boolean
    hasMultiTier: boolean
    showProductSwitcher: boolean
    hasPlacedDeposit: boolean
    skipDocuments: boolean
    poiVerified: boolean
    onboardingFlowType: NameDto
  }
  mainIbScheme: NameDto
  multiTierRegistrationAllowed: boolean
  clientProductStates: ClientProductStatesDto[]
}

export interface ClientNationalityDto {
  id: string
  name: string
  isPrimary: boolean
}

export interface ClientPlatformLimitDto {
  platformTypeId: PlatformTypeId
  liveTradingAccountLimit: string
  demoTradingAccountLimit: string
  tradingViewCouponParameters?: TradingViewCouponParametersDto
  tradingViewPlan?: NameDto<number> | null
  tradingViewPlanExpirationDays?: NameDto<number> | null
  tradingViewPlanAssignmentDate?: string | null
}

export interface UpdateClientPlatformLimitDto {
  liveAndDemoMaxTradingAccounts: ClientPlatformLimitDto[]
}

export interface TradingViewCouponParametersDto {
  tradingViewPlanId: number | null
  tradingViewPlanExpirationDaysId: number | null
}

export interface ClientPhoneNumberDto {
  id?: string
  tempId?: string
  type: string
  countryCode: string
  number: string
  isPrimary: boolean
  isVerified: boolean
  isUsedIn2FA: boolean
}

export interface ClientEmailDto {
  id?: string
  tempId?: string
  emailType: string
  address: string
  isPrimary: boolean
  isUsedIn2FA: boolean
}

export interface ClientLeadTeamDto {
  id: string
  name: string
  isPrimary: boolean
}

export interface ClientAddressDto {
  id?: string
  tempId?: string
  type: string
  street: string | null
  city: string | null
  streetHouseNo: string | null
  state: string | null
  postalCode: string | null
  country: {
    id: string
    name: string
  } | null
  isPrimary: boolean
}

interface ClientAnswerDto {
  question: string
  answer: string
}

export interface CompanyDetails {
  name: string
  registrationDate: string
  registrationNumber: string
  website: string
  email: {
    emailType: string
    address: string
    isPrimary: boolean
  }
  phoneNumber: {
    type: string
    countryCode: string
    number: string
    isPrimary: boolean
  }
  legalRepresentationBasisId: {
    id: number
    name: string
  }
  legalRepresentationBasis: {
    name: string
    id: number
  }
  ownersCount: number
  address: {
    type: string
    street: string
    city: string
    state: string
    postalCode: string
    country: string
    isPrimary: boolean
  }
  legalEntityIdentifier: string
  owners: Owner[]
  directors: Director[]
  authorisedSignatories: AuthorisedSignatory[]
  authorisedTraders: AuthorisedTrader[]
  companyType: {
    id: number
    name: string
  } | null
}

export interface Owner {
  id?: string
  name: string
  ownership: number
  birthday: string
  street: string
  city: string
  state: string
  zipCode: string
  country: NameDto<string>
  nationality: NameDto<string> | null
  worldCheckStatus: NameDto | null
  truliooEIDStatus: NameDto | null
  truliooDocVStatus: NameDto | null
  taxIdentificationNumber: string
}

export interface Director {
  id?: string
  name: string
  street: string
  city: string
  state: string
  zipCode: string
  country: NameDto<string> | null
}

export interface AuthorisedSignatory {
  id?: string
  name: string
  role: string
}

export interface AuthorisedTrader {
  id?: string
  name: string
  role: string
  email: string
}

export enum ExternalSystemId {
  SugarCRM = 1,
  HubSpot = 2,
  LegacySugarCRM = 3,
  Sumsub = 4,
  IBKR = 6,
}

export interface ExternalSystem {
  id: string
  externalSystem: {
    id: ExternalSystemId
    name: 'SugarCRM' | 'HubSpot' | 'Legacy SugarCRM' | 'Sumbsub' | 'IBKR'
  }
  externalId: string
  externalUrl: string
}

export interface ClientProductStatesDto {
  tickmillProduct: NameDto
  calculatedStatus: NameDto
  submittedAppropriatenessTest: boolean
  passedAppropriatenessTest: boolean
  agreedToLegalDocuments: boolean
  setMarketDataClassification: boolean
  appropriatenessTestMaximumRetakeLimitReached: boolean
  allRequirementsMet: boolean
}

export const isClientProductCFDStates = (productStates: ClientProductStatesDto): boolean => {
  return isCFDProductType(productStates.tickmillProduct.id)
}

export const isClientProductETDStates = (productStates: ClientProductStatesDto): boolean => {
  return isETDProductType(productStates.tickmillProduct.id)
}

export enum ClientDetailedStatus {
  Activated = 4,
}

export const isClientDetailedActivatedStatus = (status: number): boolean => {
  return ClientDetailedStatus.Activated === status
}

export const clientDetailedOwnerInitialData = (): Owner => ({
  id: v4(),
  name: '',
  ownership: 0,
  birthday: '',
  street: '',
  city: '',
  state: '',
  zipCode: '',
  country: { id: '', name: '' },
  nationality: null,
  worldCheckStatus: null,
  truliooEIDStatus: null,
  truliooDocVStatus: null,
  taxIdentificationNumber: '',
})

export const clientDetailedDirectorInitialData = (): Director => ({
  id: v4(),
  name: '',
  street: '',
  city: '',
  state: '',
  zipCode: '',
  country: { id: '', name: '' },
})

export const clientDetailedAuthorisedSignatoryInitialData = (): AuthorisedSignatory => ({
  id: v4(),
  name: '',
  role: '',
})

export const clientDetailedAuthorisedTraderInitialData = (): AuthorisedTrader => ({
  id: v4(),
  name: '',
  role: '',
  email: '',
})

export type TwoFactorAuthType = {
  isEnabled: boolean
  authMedium: string | null
  isAvailable: boolean
  id: number
  name: string
}
